import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getAutoDepositStatus, getLatestAutoDepositOrder } from "@/lib/payments.functions";
import { clearPendingOrder, closeInAppBrowser, readPendingOrder } from "@/lib/payment-browser";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, XCircle, Loader2, AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/_authenticated/payment/return")({
  component: PaymentReturnPage,
  head: () => ({
    meta: [
      { title: "Deposit Status | Brothers Club" },
      { name: "description", content: "Live status of your automatic wallet deposit." },
    ],
  }),
  validateSearch: (search: Record<string, unknown>) => ({
    order: typeof search.order === "string" ? search.order : "",
  }),
});

type Status = {
  internalOrderId: string;
  status: string;
  amount: number;
  confirmedAmount: number | null;
  walletCredited: boolean;
  utr: string | null;
  failureReason: string | null;
  message: string;
};

const POLL_INTERVAL_MS = 5000;
const POLL_WINDOW_MS = 120_000;

function isTerminal(s: Status) {
  return (
    s.walletCredited ||
    ["failed", "expired", "cancelled", "amount_mismatch", "review_required"].includes(s.status)
  );
}

function PaymentReturnPage() {
  const { order: orderParam } = Route.useSearch();
  const qc = useQueryClient();
  const check = useServerFn(getAutoDepositStatus);
  const findLatest = useServerFn(getLatestAutoDepositOrder);

  const [orderId, setOrderId] = useState<string | null>(orderParam || null);
  const [state, setState] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [polling, setPolling] = useState(true);
  const [checking, setChecking] = useState(false);
  const startedAt = useRef<number>(Date.now());
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const cancelled = useRef(false);

  // Returning from the hosted payment page: make sure the in-app browser is gone.
  useEffect(() => {
    void closeInAppBrowser();
  }, []);

  /** One verification round-trip. The server is the only source of truth. */
  const runCheck = useCallback(
    async (id: string): Promise<Status | null> => {
      const res = await check({ data: { internalOrderId: id } });
      if (!res.ok || !res.data) return null;
      return res.data as Status;
    },
    [check],
  );

  const finish = useCallback(
    (s: Status) => {
      setPolling(false);
      if (s.walletCredited || ["failed", "expired", "cancelled"].includes(s.status)) clearPendingOrder();
      qc.invalidateQueries();
    },
    [qc],
  );

  // Resolve the pending order, then check immediately and re-check every 5s
  // for up to two minutes. Nothing here credits anything.
  useEffect(() => {
    cancelled.current = false;

    async function resolveOrderId(): Promise<string | null> {
      if (orderParam) return orderParam;
      const stored = readPendingOrder();
      if (stored) return stored;
      try {
        const res = await findLatest({ data: undefined });
        return res?.internalOrderId ?? null;
      } catch {
        return null;
      }
    }

    async function tick(id: string) {
      try {
        const data = await runCheck(id);
        if (cancelled.current) return;
        if (!data) {
          setError(
            "We could not identify this payment. Check Automatic Payments or contact support with your Order ID.",
          );
          setPolling(false);
          return;
        }
        setState(data);
        if (isTerminal(data)) {
          finish(data);
          return;
        }
      } catch {
        if (cancelled.current) return;
      }
      if (Date.now() - startedAt.current >= POLL_WINDOW_MS) {
        setPolling(false);
        return;
      }
      timer.current = setTimeout(() => void tick(id), POLL_INTERVAL_MS);
    }

    void (async () => {
      const id = await resolveOrderId();
      if (cancelled.current) return;
      if (!id) {
        setError("We could not identify this payment. Check Automatic Payments or contact support with your Order ID.");
        setPolling(false);
        return;
      }
      setOrderId(id);
      startedAt.current = Date.now();
      void tick(id);
    })();

    return () => {
      cancelled.current = true;
      clearTimeout(timer.current);
    };
  }, [orderParam, runCheck, findLatest, finish]);

  /** Manual re-check. Never creates a new payment order. */
  async function checkNow() {
    if (!orderId || checking) return;
    setChecking(true);
    setError(null);
    try {
      const data = await runCheck(orderId);
      if (!data) {
        setError("We could not identify this payment. Contact support with your Order ID.");
        return;
      }
      setState(data);
      if (isTerminal(data)) finish(data);
    } catch {
      setError("Could not reach the payment server. Please try again.");
    } finally {
      setChecking(false);
    }
  }

  const credited = state?.walletCredited;
  const failed = state && ["failed", "expired", "cancelled"].includes(state.status);
  const review = state && ["amount_mismatch", "review_required"].includes(state.status);
  const pending = !!state && !credited && !failed && !review;

  const heading = credited
    ? "Payment Successful"
    : failed
      ? "Payment Failed"
      : review
        ? "Payment Under Review"
        : pending
          ? "Payment Pending"
          : "Checking payment status…";

  return (
    <div className="mx-auto max-w-md space-y-4 p-4 pb-24">
      <div className="rounded-3xl border bg-card p-6 text-center">
        <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-full bg-muted">
          {credited ? (
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
          ) : failed ? (
            <XCircle className="h-8 w-8 text-destructive" />
          ) : review ? (
            <AlertTriangle className="h-8 w-8 text-amber-500" />
          ) : polling || checking ? (
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          ) : (
            <Clock className="h-8 w-8 text-muted-foreground" />
          )}
        </div>

        <h1 className="text-lg font-black">{heading}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {error ?? state?.message ?? "Checking payment status…"}
        </p>

        {state && (
          <div className="mt-4 space-y-1 rounded-2xl bg-muted/50 p-3 text-left text-xs">
            <Row label="Order ID" value={state.internalOrderId} />
            <Row label="Amount" value={`₹${state.amount}`} />
            {state.utr && <Row label="UTR" value={state.utr} />}
            <Row label="Status" value={state.status.replace(/_/g, " ")} />
          </div>
        )}

        {!credited && (
          <p className="mt-3 text-[11px] text-muted-foreground">
            Money debited but not credited? Share the Order ID above with support — nothing is ever credited twice.
          </p>
        )}

        <div className="mt-5 space-y-2">
          {!credited && !failed && (
            <Button onClick={checkNow} disabled={checking || !orderId} className="h-11 w-full" variant="secondary">
              {checking ? "Checking…" : "Check Status"}
            </Button>
          )}
          <Button asChild className="h-11 w-full gradient-primary">
            <Link to="/wallet">Go to Wallet</Link>
          </Button>
          <Button asChild variant="outline" className="h-11 w-full">
            <Link to="/">Back to Home</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold capitalize">{value}</span>
    </div>
  );
}
